import type { StreamTaskStatus } from '@atlassian/conversation-assistant-service-api/types/assistance-service';
import type {
	ExecutionSegment,
	TaskProgressState,
} from '@atlassian/conversation-assistant-store/controllers/chat/types';
import { parseExecutionSegments } from '@atlassian/conversation-assistant-store/controllers/unified-tasks';

import type { UnifiedTasksContainerDep } from './types';

/** Minimal target surface the stream handler dispatches into. */
export type StreamTaskStatusTarget = {
	createTaskFromStream: (args: {
		conversationId: string;
		taskId: string;
		title: string;
		updatedAt?: string;
		messageId?: string;
		currentMessageId?: string | null;
		createdAt?: string;
		startedAt?: string | null;
		completedAt?: string | null;
		executionSegments?: ExecutionSegment[] | null;
	}) => void;
	setTaskProgressState: (args: {
		conversationId: string;
		progressState: TaskProgressState;
		title?: string;
		updatedAt?: string;
		seekingCompletedAck?: boolean;
		messageId?: string;
		completionMessageId?: string;
		taskId?: string;
		currentMessageId?: string | null;
		createdAt?: string;
		startedAt?: string | null;
		completedAt?: string | null;
		executionSegments?: ExecutionSegment[] | null;
	}) => void;
};

type HandleStreamTaskStatusParams = {
	conversationId: string;
	taskMessage: StreamTaskStatus['message'];
	messageId?: string;
	target: StreamTaskStatusTarget;
};

/**
 * Shared handler for TASK_STATUS stream messages.
 *
 * Maps the API task status to the appropriate target action and dispatches it.
 * Used by `sendMessage`, `sendMessageConfirmedTools`, and `reconnect` to avoid
 * duplicating the status → action mapping logic.
 */
export const handleStreamTaskStatus = ({
	conversationId,
	taskMessage,
	messageId,
	target,
}: HandleStreamTaskStatusParams): void => {
	const executionSegments = parseExecutionSegments(taskMessage.execution_segments);

	switch (taskMessage.status) {
		case 'DRAFT':
			target.createTaskFromStream({
				conversationId,
				taskId: taskMessage.task_id,
				title: taskMessage.title,
				updatedAt: taskMessage.updated_at,
				messageId,
				currentMessageId: taskMessage.current_message_id,
				createdAt: taskMessage.created_at,
				startedAt: taskMessage.started_at,
				completedAt: taskMessage.completed_at,
				executionSegments,
			});
			break;
		case 'COMPLETED':
			target.setTaskProgressState({
				conversationId,
				progressState: 'Completed',
				title: taskMessage.title,
				updatedAt: taskMessage.updated_at,
				seekingCompletedAck: true,
				completionMessageId: taskMessage.completion_message_id ?? messageId,
				currentMessageId: taskMessage.current_message_id,
				createdAt: taskMessage.created_at,
				startedAt: taskMessage.started_at,
				completedAt: taskMessage.completed_at,
				executionSegments,
			});
			break;
		case 'FAILED':
			target.setTaskProgressState({
				conversationId,
				progressState: 'Failed',
				title: taskMessage.title,
				updatedAt: taskMessage.updated_at,
				messageId,
				currentMessageId: taskMessage.current_message_id,
				createdAt: taskMessage.created_at,
				startedAt: taskMessage.started_at,
				completedAt: taskMessage.completed_at,
				executionSegments,
			});
			break;
		case 'CANCELLED':
			target.setTaskProgressState({
				conversationId,
				progressState: 'Cancelled',
				title: taskMessage.title,
				updatedAt: taskMessage.updated_at,
				messageId,
				currentMessageId: taskMessage.current_message_id,
				createdAt: taskMessage.created_at,
				startedAt: taskMessage.started_at,
				completedAt: taskMessage.completed_at,
				executionSegments,
			});
			break;
		case 'RUNNING':
			target.setTaskProgressState({
				conversationId,
				progressState: 'Running',
				title: taskMessage.title,
				updatedAt: taskMessage.updated_at,
				messageId,
				currentMessageId: taskMessage.current_message_id,
				createdAt: taskMessage.created_at,
				startedAt: taskMessage.started_at,
				completedAt: taskMessage.completed_at,
				executionSegments,
			});
			break;
		case 'AWAITING_ACTION_CONFIRMATION':
			target.setTaskProgressState({
				conversationId,
				progressState: 'AwaitingActionConfirmation',
				title: taskMessage.title,
				updatedAt: taskMessage.updated_at,
				messageId,
				currentMessageId: taskMessage.current_message_id,
				createdAt: taskMessage.created_at,
				startedAt: taskMessage.started_at,
				completedAt: taskMessage.completed_at,
				executionSegments,
			});
			break;
		default:
			break;
	}
};

/** Builds a {@link StreamTaskStatusTarget} from the unified-tasks container dep. */
export const buildUnifiedStreamTaskTarget = (
	unifiedTasksActions: UnifiedTasksContainerDep,
): StreamTaskStatusTarget => ({
	createTaskFromStream: (args) => unifiedTasksActions.createTaskFromStream(args),
	setTaskProgressState: (args) => unifiedTasksActions.setTaskProgressState(args),
});
